/**
 * NChatContext
 *
 * Hoists the useNChat hook (WebSocket connection, message state, thread management)
 * to the N71 page level so the chat "brain" stays alive regardless of whether the
 * NChat UI component is mounted.
 *
 * Previously, useNChat was called *inside* NChat.tsx. Because NChat lived inside
 * AnimatePresence with different keys per viewMode, switching views unmounted NChat,
 * tore down the WebSocket, and killed in-flight agent runs.
 *
 * Now:
 *   <NChatProvider>          ← always mounted at N71 level; WebSocket lives here
 *     <NChat />              ← pure UI; mounts/unmounts freely via viewMode
 *   </NChatProvider>
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useNChat, type UseNChatReturn } from '@/hooks/useNChat';

// ============================================
// CONTEXT
// ============================================

const NChatContext = createContext<UseNChatReturn | null>(null);

// ============================================
// PROVIDER
// ============================================

export function NChatProvider({ children }: { children: ReactNode }) {
  const nChat = useNChat();

  return (
    <NChatContext.Provider value={nChat}>
      {children}
    </NChatContext.Provider>
  );
}

// ============================================
// CONSUMER HOOK
// ============================================

/**
 * Access the N Agent chat state from any component inside <NChatProvider>.
 * Throws if used outside of the provider — this is intentional; it's always a bug.
 */
export function useNChatContext(): UseNChatReturn {
  const ctx = useContext(NChatContext);
  if (!ctx) {
    throw new Error(
      'useNChatContext must be used within <NChatProvider>. ' +
      'Wrap your component tree (e.g. at the N71 page level) with <NChatProvider>.'
    );
  }
  return ctx;
}
